import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import memoizeOne from 'memoize-one';
import { Col } from 'antd';
import differenceBy from 'lodash/differenceBy';

import { getActiveChatItem, getActiveChatMessages, getActiveChatTypingInfo, getActiveChatSelectedTags, getActiveChatTags } from '../../../../../../data/redux/chats_details/selectors';
import { startPageLoading, stopPageLoading } from '../../../../../../data/redux/page_details/actions';
import {
  updateChatItem,
  emitChatsCustomEvent,
  deleteChatItem,
  updateChatStateValue,
  getTagsByGroup,
  closeChatInterface,
  addSelectedTag,
  removeSelectedTag
} from '../../../../../../data/redux/chats_details/actions';

import { CHAT_STATE, SOCKET_EVENTS, INPUT_COMPOSER_TYPE, METHOD_TYPES, APP_LAYOUT } from '../../../../../../data/config/constants';
import { showMessage } from '../../../../../../data/config/utils';
import { IMG_CHATTING } from '../../../../../../data/assets/img';

import ChatInterface from '../../../../components/chatinterface';
import ShowMessage from '../../../../../../components/showmessage';

class ChatWindow extends React.PureComponent {
  componentDidUpdate(prevProps) {
    const { actions, admin_id } = this.props;
    const { selected_active_chat_id, active_chat_item, active_chat_tags } = this.props;
    if (selected_active_chat_id && prevProps.selected_active_chat_id !== selected_active_chat_id && active_chat_item) {
      if (active_chat_item.group && active_chat_tags.length === 0)
        actions.getTagsByGroup({ group: active_chat_item.group, psid: selected_active_chat_id });
      if (active_chat_item.chatState === CHAT_STATE.AGENT_HANDLING && active_chat_item.agentId === admin_id) {
        const payload = {
          clientPsid: selected_active_chat_id,
          senderPsid: admin_id,
        };
        actions.emitChatsCustomEvent(SOCKET_EVENTS.MESSAGE_SEEN, payload);
      }
    }
  }

  filterTagsBySelectedTags = memoizeOne((tags, selected_tags) => differenceBy(tags, selected_tags, '_id'));

  handleChatEvent = (eventName, data, callback) => {
    const { admin_id, actions } = this.props;
    const { selected_active_chat_id, active_chat_item, joinChatRoom } = this.props;
    if (selected_active_chat_id) {
      if (eventName === SOCKET_EVENTS.SUPERVISE) {
        const payload = {
          clientPsid: selected_active_chat_id,
          agentPsid: active_chat_item.agentId,
          supervisorPsid: admin_id,
        };
        actions.emitChatsCustomEvent(eventName, payload, (err, res) => {
          if (!err && res && res.data && res.data.supervisedChat)
            actions.updateChatItem(res.data.supervisedChat);
          else
            showMessage('error', res && res.message ? res.message : "supervise event error occured.");
        })
      } else if (eventName === SOCKET_EVENTS.STOP_SUPERVISING) {
        const payload = {
          clientPsid: selected_active_chat_id,
          agentPsid: active_chat_item.agentId,
          supervisorPsid: admin_id,
        };
        actions.emitChatsCustomEvent(eventName, payload, (err, res) => {
          if (!err && res && res.data && res.data.stopSupervisedChat)
            actions.updateChatItem(res.data.stopSupervisedChat);
          else
            showMessage('error', res && res.message ? res.message : "stop supervise event error occured.");
        });
      } else if (eventName === SOCKET_EVENTS.RESOLVE_CHAT) {
        const payload = {
          clientPsid: selected_active_chat_id,
          agentPsid: active_chat_item.agentId,
        };
        actions.emitChatsCustomEvent(eventName, payload, (err, res) => {
          if (!err && res && res.data && res.data.resolvedChatPsid)
            actions.deleteChatItem({ psid: res.data.resolvedChatPsid }, "chat has been successfully resolved");
          else
            showMessage('error', res && res.message ? res.message : "resolve chat event error occured.");
        });
      } else if (eventName === SOCKET_EVENTS.FORCE_TAKEOVER) {
        const payload = {
          clientPsid: selected_active_chat_id,
          agentPsid: active_chat_item.agentId,
        };
        actions.emitChatsCustomEvent(SOCKET_EVENTS.FORCE_TAKEOVER, payload);
      } else if (eventName === SOCKET_EVENTS.JOIN_ROOM) {
        const chat_info = {
          psid: selected_active_chat_id,
          chatState: active_chat_item.chatState,
          agentId: active_chat_item.agentId,
        };
        joinChatRoom(chat_info);
        if (active_chat_item.group)
          actions.getTagsByGroup({ group: active_chat_item.group, psid: selected_active_chat_id });
      } else if (eventName === SOCKET_EVENTS.ESCALATE || eventName === SOCKET_EVENTS.BLANK) {
        const payload = {
          clientPsid: selected_active_chat_id,
          [eventName]: data,
        };
        actions.startPageLoading("updating");
        actions.emitChatsCustomEvent(eventName, payload, (err, res) => {
          actions.stopPageLoading();
          if (!err && res && res.data && res.data.psid) {
            callback(false);
          }
        });
      }
    }
  };

  handleMessageSend = (text, type, text_type, cnId) => {
    const { actions } = this.props;
    const { selected_active_chat_id, active_chat_item } = this.props;
    if (selected_active_chat_id) {
      if (type === INPUT_COMPOSER_TYPE.WHISPER) {
        const payload = {
          cnId,
          message: text,
          clientPsid: selected_active_chat_id,
          agentPsid: active_chat_item.agentId,
          textType: text_type,
          timestamp: new Date().getTime()
        };
        actions.emitChatsCustomEvent(SOCKET_EVENTS.WHISPER, payload);
      } else {
        const payload = {
          cnId,
          text,
          psid: selected_active_chat_id,
          textType: text_type,
          timestamp: new Date().getTime()
        };
        actions.emitChatsCustomEvent(SOCKET_EVENTS.ADMIN_TO_USER_MSG, payload);
      }
    }
  };

  updateSelectedTag = (payload, method_type) => {
    const { actions } = this.props;
    if (payload.clientPsid) {
      if (method_type === METHOD_TYPES.PUT) {
        actions.emitChatsCustomEvent(SOCKET_EVENTS.ADD_TAG_TO_CHAT, payload, (err, res) => {
          if (err)
            showMessage('error', res && res.message ? res.message : "due to some error, unable to add tag to this chat")
          else if (res && res.data && res.data.addedTag)
            actions.addSelectedTag({ psid: payload.clientPsid, addedTag: res.data.addedTag });
        });
      } else if (method_type === METHOD_TYPES.DELETE) {
        actions.emitChatsCustomEvent(SOCKET_EVENTS.REMOVE_TAG_FROM_CHAT, payload, (err, res) => {
          if (err)
            showMessage('error', res && res.message ? res.message : "due to some error, unable to remove tag from this chat")
          else if (res && res.data && res.data.removedTag)
            actions.removeSelectedTag({ psid: payload.clientPsid, removedTag: res.data.removedTag });
        });
      }
    }
  };

  sendTypingIndication = isTyping => {
    const { selected_active_chat_id } = this.props;
    if (selected_active_chat_id) {
      const payload = {
        isTyping,
        clientPsid: selected_active_chat_id,
      };
      this.props.actions.emitChatsCustomEvent(SOCKET_EVENTS.TYPING_STATUS, payload, () => { });
    }
  };

  render() {
    const {
      screen_width,
      screen_height,
      actions,
      active_chat_item,
      active_chat_messages,
      active_chat_selected_tags,
      active_chat_tags,
      active_chat_typing_info,
      tags_loading,
      admin_id,
      isActionPermitted,
      openTransferChatDrawer
    } = this.props;
    const is_mobile = screen_width < 768;
    if (active_chat_item) {
      const tags = this.filterTagsBySelectedTags(active_chat_tags, active_chat_selected_tags);
      return (
        <Col
          xs={24}
          md={12}
          id='chatWindow'
          className={`${is_mobile ? "ori-bg-white ori-b-mrgn-10" : "ori-full-parent-height"}`}
          style={is_mobile ? { height: `${screen_height - APP_LAYOUT.APP_HEADER_HEIGHT}px` } : {}}
        >
          <ChatInterface
            isActionPermitted={isActionPermitted}
            messages={active_chat_messages}
            active_chat_item={active_chat_item}
            onClickClose={actions.closeChatInterface}
            onClickTransfer={openTransferChatDrawer}
            show_composer_type={(active_chat_item.chatState === CHAT_STATE.AGENT_HANDLING) && (active_chat_item.agentId !== admin_id) && (active_chat_item.supervisorId === admin_id)}
            show_chat_composer={((active_chat_item.agentId === admin_id) || ((active_chat_item.agentId !== admin_id) && (active_chat_item.supervisorId === admin_id))) && (active_chat_item.chatState === CHAT_STATE.AGENT_HANDLING)}
            handleMessageSend={this.handleMessageSend}
            handleChatEvent={this.handleChatEvent}
            tags={tags}
            tags_loading={tags_loading}
            selected_tags={active_chat_selected_tags}
            updateSelectedTag={this.updateSelectedTag}
            sendTypingIndication={this.sendTypingIndication}
            typing_info={active_chat_typing_info}
          />
        </Col>
      );
    }
    return (
      <Col
        xs={24}
        md={12}
        className={`${is_mobile ? "ori-bg-white ori-b-mrgn-10" : "ori-full-parent-height"}`}
      >
        <ShowMessage
          img_src={IMG_CHATTING}
          title="Conversations"
          message="Please click on chat item to see live conversation details."
          height={180}
          width={200}
        />
      </Col>
    );
  }
}

const mapStateToProps = state => {
  return {
    screen_width: state.page_details.device_data.screen_width,
    screen_height: state.page_details.device_data.screen_height,
    admin_id: state.admin_details.admin_id,
    selected_active_chat_id: state.chats_details.selected_active_chat_id,
    tags_loading: state.chats_details.loaders.tags_loading,
    active_chat_item: getActiveChatItem(state),
    active_chat_messages: getActiveChatMessages(state),
    active_chat_tags: getActiveChatTags(state),
    active_chat_selected_tags: getActiveChatSelectedTags(state),
    active_chat_typing_info: getActiveChatTypingInfo(state),
  };
};

const mapDispatchToProps = dispatch => {
  return {
    actions: bindActionCreators(
      {

        startPageLoading,
        stopPageLoading,
        updateChatItem,
        deleteChatItem,
        emitChatsCustomEvent,
        updateChatStateValue,
        getTagsByGroup,
        closeChatInterface,
        addSelectedTag,
        removeSelectedTag,
      },
      dispatch),
  };
};

ChatWindow.propTypes = {
  screen_width: PropTypes.number,
  screen_height: PropTypes.number,
  admin_id: PropTypes.string,
  actions: PropTypes.object,
  isActionPermitted: PropTypes.func,
  openTransferChatDrawer: PropTypes.func,
  active_chat_messages: PropTypes.array,
  active_chat_tags: PropTypes.array,
  active_chat_selected_tags: PropTypes.array,
  active_chat_typing_info: PropTypes.object,
  active_chat_item: PropTypes.object,
  selected_active_chat_id: PropTypes.string,
  tags_loading: PropTypes.bool,
  joinChatRoom: PropTypes.func,
};

ChatWindow.defaultProps = {
  active_chat_messages: [],
  active_chat_tags: [],
  active_chat_selected_tags: [],
  active_chat_typing_info: {},
}

export default connect(mapStateToProps, mapDispatchToProps)(ChatWindow);



// WEBPACK FOOTER //
// ./src/app/pagecontainer/modules/chats/components/chatwindow/index.js